import Component from '@glimmer/component';
import { get } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { and } from 'tio-ui/utilities';

/**
 * Size options consistent with all reimagined form components.
 */
export type FormFieldSize = 'sm' | 'md' | 'lg';

interface FormFieldSignature {
  Args: {
    /** Label text displayed above the input */
    label?: string;
    /** Hint text displayed below the input */
    hint?: string;
    /** Array of error messages to display */
    errors?: string[];
    /** Show a red asterisk next to the label */
    isRequired?: boolean;
    /** Apply disabled styling */
    isDisabled?: boolean;
    /** Size variant @default "md" */
    size?: FormFieldSize;
    /** Explicit field ID for label[for] → input[id] association. Auto-generated if omitted. */
    fieldId?: string;
  };
  Blocks: {
    /** Rendered inside the label row after the text — e.g. an "Optional" qualifier.
     * A separate block because the wrapper is a flex column: anything rendered in the
     * default block lands on its own row below the label. Only rendered when `@label`
     * is present, since it qualifies the label rather than the field. */
    labelSuffix?: [];
    /** Receives { fieldId, hasErrors, isRequired } for input rendering */
    default: [{ fieldId: string; hasErrors: boolean; isRequired: boolean }];
  };
  Element: HTMLDivElement;
}

const LABEL_SIZES: Record<FormFieldSize, string> = {
  sm: 'tio-text-xs-medium',
  md: 'tio-text-sm-medium',
  lg: 'tio-text-sm-medium',
};

/**
 * FormField — Shared wrapper used internally by Input, Textarea, Checkbox, etc.
 * Can also be used standalone for custom field layouts.
 *
 * Renders a consistent label + hint + error shell around any form input.
 * Yields `{ fieldId, hasErrors, isRequired }` so the wrapped input can
 * bind `id`, `aria-invalid`, and `aria-required`.
 *
 * @example Standalone usage
 * ```gts
 * import FormField from 'tio-ui/components/form-field';
 *
 * <FormField @label="Custom Field" @hint="Enter a value" @errors={{this.errors}} as |field|>
 *   <input id={{field.fieldId}} type="text" aria-invalid={{if field.hasErrors "true"}} />
 * </FormField>
 * ```
 */
class FormField extends Component<FormFieldSignature> {
  /** Stable per-instance ID that won't change across re-renders */
  private _generatedId = `reimagined-field-${guidFor(this)}`;

  private get fieldId(): string {
    return this.args.fieldId ?? this._generatedId;
  }

  <template>
    {{#let
      this.fieldId (if @size @size "md") (if (and @errors @errors.length) true false)
      as |fieldId size hasErrors|
    }}
      <div
        data-test-form-field
        class="tio-col-extra-tight {{if @isDisabled 'opacity-50'}}"
        ...attributes
      >
        {{! Label }}
        {{#if @label}}
          <label
            data-test-form-field-label
            for={{fieldId}}
            class="{{get LABEL_SIZES size}} tio-fg-secondary"
          >
            {{@label}}
            {{#if @isRequired}}
              <span class="tio-fg-danger ml-0.5" aria-hidden="true">*</span>
            {{/if}}
            {{#if (has-block "labelSuffix")}}
              {{yield to="labelSuffix"}}
            {{/if}}
          </label>
        {{/if}}

        {{! Input slot }}
        {{yield (hash fieldId=fieldId hasErrors=hasErrors isRequired=(if @isRequired true false))}}

        {{! Hint text }}
        {{#if @hint}}
          <p data-test-form-field-hint id="{{fieldId}}-hint" class="text-xs tio-fg-muted">
            {{@hint}}
          </p>
        {{/if}}

        {{! Error messages }}
        {{#if hasErrors}}
          <div data-test-form-field-errors class="space-y-0.5">
            {{#each @errors as |error|}}
              <p class="text-xs tio-fg-danger" role="alert">
                {{error}}
              </p>
            {{/each}}
          </div>
        {{/if}}
      </div>
    {{/let}}
  </template>
}

export default FormField;
export type { FormFieldSignature };
